import { HardHat, Wrench, Package, Settings, Factory, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { SectionHeading } from "@/components/ui/section-heading";

const servicesEn = [
  {
    icon: HardHat,
    title: "Manpower Supply",
    description: "Skilled and semi-skilled manpower for construction and industrial projects.",
    bullets: ["Helpers", "Technicians", "Operators", "General workforce", "Shutdown support teams"],
    href: "/services/contracting",
  },
  {
    icon: Wrench,
    title: "Equipment Supply",
    description: "Equipment supplied based on project requirements with quick arrangement.",
    bullets: ["Construction equipment", "Industrial tools", "Lifting support equipment"],
    href: undefined,
  },
  {
    icon: Package,
    title: "Material Supply",
    description: "Required materials for construction and industrial operations.",
    bullets: ["Industrial materials", "Construction supplies", "Project-based requirements"],
    href: "/services/trading",
  },
  {
    icon: Settings,
    title: "Shutdown & Maintenance",
    description: "Planned support for shutdown and maintenance activities.",
    bullets: ["Turnaround crews", "Preventive maintenance", "Mechanical support"],
    href: undefined,
  },
  {
    icon: Factory,
    title: "Live Plant Support",
    description: "Experienced workforce for operational facilities.",
    bullets: ["Plant operations crews", "Routine site support", "Long-term deployment"],
    href: undefined,
  },
];

const servicesAr = [
  { icon: HardHat, title: "توريد العمالة", description: "عمالة ماهرة وشبه ماهرة لمشاريع البناء والصناعة.", bullets: ["مساعدون", "فنيون", "مشغلون", "عمالة عامة", "فرق دعم الإيقاف"], href: "/services/contracting" },
  { icon: Wrench, title: "توريد المعدات", description: "توريد المعدات حسب متطلبات المشروع مع ترتيب سريع.", bullets: ["معدات البناء", "أدوات صناعية", "معدات دعم الرفع"], href: undefined },
  { icon: Package, title: "توريد المواد", description: "توفير المواد اللازمة لعمليات البناء والصناعة.", bullets: ["مواد صناعية", "مستلزمات البناء", "متطلبات حسب المشروع"], href: "/services/trading" },
  { icon: Settings, title: "الإيقاف والصيانة", description: "دعم مخطط لأنشطة الإيقاف والصيانة.", bullets: ["فرق أعمال التوقف", "الصيانة الوقائية", "الدعم الميكانيكي"], href: undefined },
  { icon: Factory, title: "دعم المصانع التشغيلية", description: "عمالة ذات خبرة للمنشآت العاملة.", bullets: ["فرق تشغيل المصانع", "دعم الموقع الدوري", "انتداب طويل الأمد"], href: undefined },
];

const Services = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const services = isAr ? servicesAr : servicesEn;
  const eyebrow = isAr ? "ماذا نقدّم" : "What we do";
  const heading1 = isAr ? "خدماتنا" : "Our";
  const heading2 = isAr ? "المتكاملة" : "Services";
  const learnMore = isAr ? "اعرف المزيد" : "Learn more";
  const ctaText = isAr ? "تحتاج دعمًا لمشروعك؟" : "Need support for your project?";
  const ctaWa = isAr ? "واتساب" : "WhatsApp";
  const ctaCall = isAr ? "اتصل" : "Call";
  return (
    <section id="services" className="py-20 bg-background">
      <div className="container mx-auto px-6 lg:px-8">
        <SectionHeading
          centered
          eyebrow={eyebrow}
          accent={heading2 || undefined}
          wrapperClassName="mb-14 max-w-2xl"
        >
          {heading1}
        </SectionHeading>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {services.map((s) => {
            const Card = (
              <div className="group h-full bg-card border border-border rounded-xl p-6 text-center hover:border-accent hover:shadow-elegant transition-smooth">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 group-hover:bg-accent/15 flex items-center justify-center mb-4 transition-smooth">
                  <s.icon className="w-8 h-8 text-primary group-hover:text-accent transition-smooth" />
                </div>
                <h3 className="text-lg font-bold text-primary-darker mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
                {s.bullets && s.bullets.length > 0 && (
                  <ul className={`mt-4 space-y-1.5 text-sm text-muted-foreground ${isAr ? 'text-right' : 'text-left'}`}>
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.href && (
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-accent">
                    {learnMore} <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </div>
            );
            return s.href ? (
              <Link key={s.title} to={s.href} aria-label={s.title}>{Card}</Link>
            ) : (
              <div key={s.title}>{Card}</div>
            );
          })}
        </div>

        <div className="mt-12 bg-primary-darker text-primary-foreground rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-elegant">
          <p className="text-lg sm:text-xl font-bold text-center sm:text-left">
            {ctaText} <span className="text-accent">+966 50 483 8870</span>
          </p>
          <div className="flex gap-3">
            <a
              href="https://wa.me/966504838870?text=Hello%20I%20need%20manpower%20support"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold px-5 py-2.5 rounded-md text-sm uppercase tracking-wide transition-colors"
            >
              {ctaWa}
            </a>
            <a
              href="tel:+966504838870"
              className="bg-accent hover:bg-accent/90 text-primary-darker font-bold px-5 py-2.5 rounded-md text-sm uppercase tracking-wide transition-colors"
            >
              {ctaCall}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;